import axios from 'axios';
import { formatDateOnlyLocal } from '../utils/dateOnly';
import { debugLog } from '../config/debug';
import { SESSION_EXPIRED_EVENT } from '../utils/loginRedirect';
import { getApiBaseUrl } from '../utils/apiConfig';

const API_BASE_URL = getApiBaseUrl();

// Axios Instanz mit Standard-Konfiguration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // 10 Sekunden Timeout
  withCredentials: true, // 🔒 Session-Cookies immer mitsenden
  // KEIN Default Content-Type Header! Axios setzt automatisch den richtigen Header basierend auf den Daten
  // (application/json für Objekte, multipart/form-data für FormData)
});

// Request Interceptor für Debugging
api.interceptors.request.use(
  (config) => {
    // WICHTIG: FormData nicht normalisieren (für File-Uploads)
    if (config?.data instanceof FormData) {
      debugLog.api(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`, '[FormData]');
      return config;
    }
    
    // Date-only Normalisierung: verhindert "Vortag" durch toISOString() bei lokalen Mitternachts-Dates
    const normalizePayload = (value, keyHint = '') => {
      if (value === null || value === undefined) return value;

      if (value instanceof Date) {
        const isMidnightLocal =
          value.getHours() === 0 &&
          value.getMinutes() === 0 &&
          value.getSeconds() === 0 &&
          value.getMilliseconds() === 0;
        return isMidnightLocal ? formatDateOnlyLocal(value) : value.toISOString();
      }

      if (typeof value === 'string') {
        const key = String(keyHint || '');
        const looksLikeIso = value.includes('T') && /Z$/.test(value);
        const isLikelyDateOnlyKey =
          /(^|_)(datum|faellig|faelligkeit|gueltig|ablese|installationsdatum|eichdatum|startdatum|enddatum)(_|$)/i.test(key) ||
          /(_am|_von|_bis)$/i.test(key);
        if (looksLikeIso && isLikelyDateOnlyKey) {
          return value.split('T')[0];
        }
        return value;
      }

      if (Array.isArray(value)) {
        return value.map((v) => normalizePayload(v, keyHint));
      }

      if (typeof value === 'object') {
        const out = {};
        for (const [k, v] of Object.entries(value)) {
          out[k] = normalizePayload(v, k);
        }
        return out;
      }

      return value;
    };

    if (config?.data && typeof config.data === 'object') {
      config.data = normalizePayload(config.data);
    }

    debugLog.api(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`, config.data);
    return config;
  },
  (error) => {
    debugLog.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response Interceptor für Fehlerbehandlung mit 401 Redirect
api.interceptors.response.use(
  (response) => {
    debugLog.api(`✅ API Response: ${response.status}`, response.data);
    // Gib direkt response.data zurück für einfachere Verwendung
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;
    
    debugLog.error('❌ API Response Error:', error);
    
    // 401 Unauthorized - Session abgelaufen
    if (error.response?.status === 401) {
      const requestUrl = String(originalRequest?.url || '');
      // Öffentliche Auth-Endpunkte dürfen ohne Session mit 401/400 antworten
      const isPublicAuthEndpoint =
        requestUrl.includes('/api/auth/login') ||
        requestUrl.includes('/api/auth/verify') ||
        requestUrl.includes('/api/auth/password-reset/') ||
        requestUrl.includes('/api/auth/invitation-preview') ||
        requestUrl.includes('/api/csrf-token');
      
      if (!isPublicAuthEndpoint && !originalRequest._retry) {
        debugLog.api('🔄 [API] 401 Unauthorized - Versuche Session-Revalidierung...');
        originalRequest._retry = true;
        
        // Kurz warten, dann Retry (für Race-Conditions bei Initial-Load)
        await new Promise(resolve => setTimeout(resolve, 300));
        
        try {
          debugLog.api('🔄 [API] Retrying request:', originalRequest.url);
          const response = await api.request(originalRequest);
          debugLog.api('✅ [API] Retry erfolgreich!');
          return response;
        } catch (retryError) {
          debugLog.error('❌ [API] Retry fehlgeschlagen:', retryError);
          throw retryError;
        }
      } else if (!isPublicAuthEndpoint) {
        debugLog.api('🚪 [API] Session definitiv ungültig');
        window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      }
    }
    
    // Spezifische Fehlerbehandlung
    if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
      debugLog.error('🔥 Backend Server ist nicht erreichbar! Prüfe CORS/Netzwerk und die API-Base-URL.');
      throw new Error('Backend ist nicht erreichbar. Bitte prüfe Netzwerk/CORS und die API-URL.');
    }
    
    if (error.response) {
      // Server hat geantwortet, aber mit einem Fehlercode
      const { status, data } = error.response;
      debugLog.error(`🔥 Server Error ${status}:`, data);
      const newErr = new Error(data.message || data.error || `Server Error: ${status}`);
      newErr.response = error.response;
      newErr.data = data;
      throw newErr;
    } else if (error.request) {
      // Request wurde gesendet, aber keine Antwort erhalten
      debugLog.error('🔥 Keine Antwort vom Server erhalten:', error.request);
      throw new Error('Keine Antwort vom Server erhalten. Prüfe die Internetverbindung.');
    } else {
      // Fehler beim Aufsetzen des Requests
      debugLog.error('🔥 Request Setup Error:', error.message);
      throw new Error(`Request Fehler: ${error.message}`);
    }
  }
);

// API Service Funktionen
export const apiService = {
  // Generische HTTP-Methoden für direkte API-Aufrufe
  get: (url, config = {}) => api.get(url, config),
  post: (url, data = {}, config = {}) => api.post(url, data, config),
  put: (url, data = {}, config = {}) => api.put(url, data, config),
  patch: (url, data = {}, config = {}) => api.patch(url, data, config),
  delete: (url, config = {}) => api.delete(url, config),
  
  // Bankwesen Übersicht
  getBankwesenUebersicht: () => api.get('/api/bankwesen/uebersicht'),
  // KPI: Ertrag je Verwaltungsstunde (Immobilien)
  getVerwaltungImmobilienKpi: () => api.get('/api/kpi/verwaltung-immobilien'),
  // KPI: Strategiezeit-Anteil (Immobilien)
  getImmobilienStrategieKpi: () => api.get('/api/kpi/immobilien-strategie'),
  // KPI: Corporate-Job-Zeitanteil (Immobilien)
  getImmobilienCorporateJobKpi: () => api.get('/api/kpi/immobilien-corporate-job'),
  // Bankwesen Verträge
  getBankwesenVertraege: (params = {}) => api.get('/api/bankwesen/vertraege', { params }),
  createBankwesenVertrag: (data) => api.post('/api/bankwesen/vertraege', data),
  updateBankwesenVertrag: (id, data) => api.put(`/api/bankwesen/vertraege/${id}`, data),
  deleteBankwesenVertrag: (id) => api.delete(`/api/bankwesen/vertraege/${id}`),
  getBankwesenObjekte: () => api.get('/api/bankwesen/objekte'),
  createDarlehensVorlage: (id, data) => api.post(`/api/bankwesen/vertraege/${id}/vorlage-erzeugen`, data),

  // Objekte & Wohneinheiten
  getObjects: () => api.get('/api/objects'),
  getWohneinheiten: () => api.get('/api/wohneinheiten'),
  getWohneinheitenByObject: (objektId) => api.get(`/api/wohneinheiten/object/${objektId}`),

  // Kontobewegungen
  getKontobewegungen: () => api.get('/api/kontobewegung'),
  
  // CSV Upload
  uploadCsv: (formData) => api.post('/api/kontobewegungcsv/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 120000, // 2 Minuten Timeout für große CSV-Dateien
  }),
  
  // Bar Transaktionen
  addBarTransaction: (data) => api.post('/api/kontobewegungbar/add', data),
  
  // Belege
  getBelege: () => api.get('/api/documents'),
  uploadBeleg: (formData) => api.post('/api/documents/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  updateBeleg: (id, data) => api.put(`/api/documents/${id}`, data),
  
  // Buchungen
  getBuchungen: () => api.get('/api/buchung'),
  createBuchung: (data) => api.post('/api/buchung/zuordnen', data),
  getOffeneZuordnungen: () => api.get('/api/buchung/offene-zuordnungen'),
  
  // Buchungskonten (Legacy-Kompatibilität, read-only)
  getBuchungskonten: (params = {}) => api.get('/api/buchungskonten', { params }),
  getUmlagefaehigeBuchungskonten: () => api.get('/api/buchungskonten/umlagefaehig'),
  createBuchungskonto: async () => {
    throw new Error('Schreiboperationen auf Legacy-Buchungskonten sind deaktiviert. Verwenden Sie /api/konten.');
  },
  updateBuchungskonto: async () => {
    throw new Error('Schreiboperationen auf Legacy-Buchungskonten sind deaktiviert. Verwenden Sie /api/konten.');
  },
  deleteBuchungskonto: async () => {
    throw new Error('Schreiboperationen auf Legacy-Buchungskonten sind deaktiviert. Verwenden Sie /api/konten.');
  },
  checkBuchungskontoCanDelete: async () => {
    throw new Error('Legacy-Buchungskonten-Loeschpruefung ist deaktiviert. Verwenden Sie /api/konten.');
  },
  
  // Konten (Neues System - Doppelte Buchführung)
  getSachkonten: () => api.get('/api/konten', { params: { konto_typ: 'SACHKONTO' } }),
  getLiquiditaetskonten: () => api.get('/api/konten', { params: { konto_typ: 'LIQUIDITAETSKONTO' } }),
  getAlleKonten: (params = {}) => api.get('/api/konten', { params }),
  createKonto: (data) => api.post('/api/konten', data),
  updateKonto: (id, data) => api.put(`/api/konten/${id}`, data),
  deleteKonto: (id) => api.delete(`/api/konten/${id}`),
  
  // Engine / Vertragsbasierte Forderungserzeugung
  runForderungenEngine: (payload = {}) => api.post('/api/forderungen-v2/engine/run', payload),

  // Server Status Check
  checkServerStatus: async () => {
    try {
      const data = await api.get('/api/health');
      return { status: 'online', data: data };
    } catch (error) {
      return { status: 'offline', error: error.message };
    }
  },

  // Projekte & Vorlagen (ToDo-Projektmodul)
  getTodoProjekte: (params = {}) => api.get('/api/todo-projekte', { params }),
  getTodoProjektById: (id) => api.get(`/api/todo-projekte/${id}`),
  createTodoProjekt: (data) => api.post('/api/todo-projekte', data),
  updateTodoProjekt: (id, data) => api.put(`/api/todo-projekte/${id}`, data),
  deleteTodoProjekt: (id) => api.delete(`/api/todo-projekte/${id}`),
  getTodoProjektGantt: (id) => api.get(`/api/todo-projekte/${id}/gantt`),
  getGlobalGantt: () => api.get('/api/todo-projekte/global/gantt'),
  getPlannerRessourcen: () => api.get('/api/todo-projekte/planner/ressourcen'),
  savePlannerRessourcen: (data) => api.put('/api/todo-projekte/planner/ressourcen', data),
  getZeitfreigaben: (params = {}) => api.get('/api/zeiterfassung/freigabe', { params }),
  updateZeitfreigabe: (id, data) => api.post(`/api/zeiterfassung/freigabe/${id}`, data),
  getGehaltsVorschau: (params = {}) => api.get('/api/gehalt/vorschau', { params }),
  createGehaltsAbrechnung: (data) => api.post('/api/gehalt/abrechnungen', data),
  exportGehaltsAbrechnung: (id, format = 'csv') => api.get(`/api/gehalt/abrechnungen/${id}/export`, { params: { format } }),
  validateTodoProjektDependencies: (id) => api.post(`/api/todo-projekte/${id}/dependencies/validate`, {}),
  createTodoProjektFromTemplate: (templateId, data) => api.post(`/api/todo-projekte/from-template/${templateId}`, data),
  createTodoProjektWorkPackage: (projektId, data) => api.post(`/api/todo-projekte/${projektId}/work-packages`, data),
  updateTodoProjektWorkPackage: (projektId, workPackageId, data) => api.put(`/api/todo-projekte/${projektId}/work-packages/${workPackageId}`, data),
  deleteTodoProjektWorkPackage: (projektId, workPackageId, mode = 'promote') => api.delete(`/api/todo-projekte/${projektId}/work-packages/${workPackageId}?mode=${encodeURIComponent(mode)}`),
  resequenceTodoProjekt: (projektId, data) => api.post(`/api/todo-projekte/${projektId}/resequence`, data),
  listTodoAbhaengigkeiten: (todoId) => api.get(`/api/vertrags-todos/${todoId}/abhaengigkeiten`),
  createTodoAbhaengigkeit: (successorTodoId, data) => api.post(`/api/vertrags-todos/${successorTodoId}/abhaengigkeiten`, data),
  deleteTodoAbhaengigkeit: (todoId, dependencyId) => api.delete(`/api/vertrags-todos/${todoId}/abhaengigkeiten/${dependencyId}`),
  getProjektVorlagen: () => api.get('/api/projekt-vorlagen'),
  createProjektVorlage: (data) => api.post('/api/projekt-vorlagen', data),
  updateProjektVorlage: (id, data) => api.put(`/api/projekt-vorlagen/${id}`, data),
  deleteProjektVorlage: (id) => api.delete(`/api/projekt-vorlagen/${id}`),

  // ToDo Zeitbuchungen
  getActiveZeitbuchung: () => api.get('/api/zeitbuchungen/aktiv'),
  getZeitbuchungenSummary: (days = 7) => api.get('/api/zeitbuchungen/summary', { params: { days } }),
  getZeitbuchungenCalendar: (params = {}) => api.get('/api/zeitbuchungen/calendar', { params }),
  listZeitbuchungen: (params = {}) => api.get('/api/zeitbuchungen', { params }),
  getTodoZeitbuchungen: (todoId) => api.get(`/api/vertrags-todos/${todoId}/zeit`),
  startTodoZeitbuchung: (todoId, data = {}) => api.post(`/api/vertrags-todos/${todoId}/zeit/start`, data),
  stopTodoZeitbuchung: (todoId, data = {}) => api.post(`/api/vertrags-todos/${todoId}/zeit/stop`, data),
  createTodoZeitbuchungManual: (todoId, data) => api.post(`/api/vertrags-todos/${todoId}/zeit/manual`, data),
  createZeitbuchung: (data) => api.post('/api/zeitbuchungen', data),
  updateZeitbuchung: (id, data) => api.put(`/api/zeitbuchungen/${id}`, data),
  deleteZeitbuchung: (id) => api.delete(`/api/zeitbuchungen/${id}`),
  sendZeitbuchungHeartbeat: () => api.post('/api/zeitbuchungen/heartbeat', {}),

  // User Notifications
  getNotifications: (params = {}) => api.get('/api/notifications', { params }),
  acknowledgeNotification: (id) => api.post(`/api/notifications/${id}/acknowledge`, {}),
  getUnreadNotificationCount: () => api.get('/api/notifications/unread-count'),

  // ToDo Geldbuchungen
  getTodoGeldbuchungen: (todoId) => api.get(`/api/vertrags-todos/${todoId}/geldbuchungen`),
  createTodoGeldbuchung: (todoId, data) => api.post(`/api/vertrags-todos/${todoId}/geldbuchungen`, data),
  updateTodoGeldbuchung: (id, data) => api.put(`/api/geldbuchungen/${id}`, data),
  deleteTodoGeldbuchung: (id) => api.delete(`/api/geldbuchungen/${id}`)
};

// 🤖 Vorlagen API mit Automatisierung
export const vorlagenApi = {
  // Standard CRUD
  getAll: (params = {}) => api.get('/api/forderungsvorlagen-v2', { params }),
  getById: (id) => api.get(`/api/forderungsvorlagen-v2/${id}`),
  create: (data) => api.post('/api/forderungsvorlagen-v2', data),
  update: (id, data) => api.put(`/api/forderungsvorlagen-v2/${id}`, data),
  delete: (id) => api.delete(`/api/forderungsvorlagen-v2/${id}`),
  
  // 🤖 Automatisierung Endpoints  
  analyzeAutomation: (id, vorlageData) => api.post(`/api/forderungsvorlagen-v2/${id}/automation-analysis`, {
    vorlageData
  }),
  
  updateAutomated: (id, data) => api.put(`/api/forderungsvorlagen-v2/${id}/automatisiert`, data),
  
  getAutomationStatus: (id) => api.get(`/api/forderungsvorlagen-v2/${id}/automation-status`)
};

// Mietanpassungen API
export const mietanpassungenApi = {
  getAll: (mietvertragId) => api.get(`/api/mietvertrag/${mietvertragId}/anpassungen`),
  create: (mietvertragId, data) => api.post(`/api/mietvertrag/${mietvertragId}/anpassungen`, data),
  update: (mietvertragId, anpassungId, data) => api.put(`/api/mietvertrag/${mietvertragId}/anpassungen/${anpassungId}`, data),
  execute: (mietvertragId, anpassungId, dryRun = false) => api.post(`/api/mietvertrag/${mietvertragId}/anpassungen/${anpassungId}/ausfuehren`, { preview: dryRun }),
  cleanup: (mietvertragId, anpassungId) => api.post(`/api/mietvertrag/${mietvertragId}/anpassungen/${anpassungId}/cleanup`),
  rollback: (mietvertragId, anpassungId) => api.post(`/api/mietvertrag/${mietvertragId}/anpassungen/${anpassungId}/rollback`)
};

export default apiService;
